/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Loads and validates appointment data before opening the virtual meeting modal.
 * @module logic/appointments/loadVirtualAppointmentCandidate
 */

import { getAppointmentDetail } from "../appointment/index.js";

/** @typedef {"not_approved"|"below_threshold"|"missing_email"|null} VirtualAppointmentBlockReason */

/**
 * Synchronous guard so UI can reject invalid requests before showing a loading state.
 * @param {Object|null|undefined} request
 * @returns {VirtualAppointmentBlockReason}
 */
export const getSyncVirtualAppointmentBlockReason = (request) =>
  request?.estado !== "approved" ? "not_approved" : null;

/**
 * @param {Object} options
 * @param {import("react-router").NavigateFunction} options.navigate
 * @param {Object} options.request List-row appointment request
 * @param {string} options.eventId
 * @returns {Promise<{ ok: true, appointment: Object }|{ ok: false, reason: VirtualAppointmentBlockReason }>}
 */
export const loadVirtualAppointmentCandidate = async ({
  navigate,
  request,
  eventId,
}) => {
  const syncBlockReason = getSyncVirtualAppointmentBlockReason(request);
  if (syncBlockReason) {
    return { ok: false, reason: syncBlockReason };
  }

  const detail = await getAppointmentDetail(navigate, request._id, eventId);
  const appointmentForVirtual = {
    ...request,
    ...detail?.appointment,
    userData: detail?.senderUser || detail?.appointment?.userData,
    canMakeVirtual:
      detail?.appointment?.canMakeVirtual ?? request.canMakeVirtual,
  };

  if (!appointmentForVirtual.canMakeVirtual) {
    return { ok: false, reason: "below_threshold" };
  }

  if (!appointmentForVirtual.userData?.correoElectronico) {
    return { ok: false, reason: "missing_email" };
  }

  return { ok: true, appointment: appointmentForVirtual };
};
